import React, {
  type FormEvent,
  SetStateAction,
  useCallback,
  useState
} from "react";

import { logger } from "src/core/globals";

// for this to work the input needs to have both name and value defined, then it'll autobind that to the state object
// directly set form state by using the setForm return function
export const useForm = <T extends object>(
  initialState: T,
  onChange?: (formData: T) => void
): {
  form: T;
  handleInputChange: (
    e: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>,
    isValueNumber?: boolean,
    hasError?: boolean
  ) => void;
  handleSelectChange: (field: string) => (value: string) => void;
  handleTagInputChange: (key: string, values: string[]) => void;
  handleToggleChange: (
    e: FormEvent<HTMLButtonElement | SVGRectElement>
  ) => void;
  handleFormValueChange: (t: T) => void;
  setForm: (o: SetStateAction<T>) => void;
  error?: string;
  setError: (o: SetStateAction<string | undefined>) => void;
} => {
  const [state, setState] = useState(initialState);
  const [error, setError] = useState<string>();

  const handleInputChange = useCallback(
    (
      e?:
        | React.FormEvent<HTMLInputElement>
        | React.FormEvent<HTMLTextAreaElement>,
      isValueNumber?: boolean,
      hasError?: boolean
    ) => {
      setState((prev) => {
        /* given the unknown nature of the form values seems to be no other way */
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const o: any = {};
        const target = e?.target as HTMLInputElement | HTMLTextAreaElement;
        if (target === undefined) {
          logger.error("Input event target undefined");
          return prev;
        }
        const index = target.name as keyof T;
        if (index === undefined) {
          logger.error(
            "Input 'name' is undefined; Make JSX property 'name' is being set."
          );
          return prev;
        }
        if (isValueNumber) {
          o[index] = target.value ? Number(target.value) : undefined;
        } else {
          o[index] = target.value;
        }
        if (target === undefined) {
          logger.error(
            "Input 'value' is undefined; Make JSX property 'value' is being set."
          );
          return prev;
        }
        if (hasError) {
          setError("");
        }
        const newState = { ...prev, ...o };
        onChange?.(newState);
        return newState;
      });
    },
    [onChange]
  );

  const handleSelectChange = useCallback(
    (field: string) => (value: string) => {
      setState((prev: T) => {
        const o: Record<string, string> = {};
        o[field] = value;
        const newState = { ...prev, ...o };
        onChange?.(newState);
        return newState;
      });
    },
    [onChange]
  );

  const handleTagInputChange = useCallback(
    (key: string, tagValues: string[]) => {
      setState((prev: T) => {
        const o: { [key: string]: string[] } = {};
        if (tagValues) {
          o[key] = tagValues;
        }
        const newState = { ...prev, ...o };
        onChange?.(newState);
        return newState;
      });
    },
    [onChange]
  );

  /**
   * Bind the toggle component boolean changes to form model.
   * Runtime validations ensure that the JSX markup and form model setup are correct.
   * Event may be the actual button (when using keyboard for accessibility) or
   * the SVG Rectangle shape when clicking with the mouse.
   */
  const handleToggleChange = useCallback(
    (e: FormEvent<HTMLButtonElement | SVGRectElement>) => {
      setState((prev) => {
        const target = e.target as HTMLElement | SVGElement;
        const button =
          target instanceof HTMLButtonElement
            ? target
            : target.closest("button");

        if (!button) {
          logger.error("Toggle Button is not found.");
          return prev;
        }

        const name = button.name as keyof T;
        if (!name) {
          logger.error("Toggle component 'name' property is undefined.");
          return prev;
        }

        const currentCheckedState = button.ariaChecked === "true";

        const newState: T = { ...prev, [name]: !currentCheckedState };
        onChange?.(newState);
        return newState;
      });
    },
    [onChange]
  );

  const handleFormValueChange = useCallback(
    (t: T) => {
      setState((prev: T) => {
        const newState = { ...prev, ...t };
        onChange?.(newState);
        return newState;
      });
    },
    [onChange]
  );

  return {
    form: state,
    handleInputChange,
    handleSelectChange,
    handleTagInputChange,
    handleToggleChange,
    handleFormValueChange,
    setForm: setState,
    error,
    setError
  };
};
